import { SignalingService, TurnCredentials } from './signaling.service';

/**
 * WebRTCService manages a WebRTC peer connection for cloud users.
 * Includes TURN server configuration for NAT traversal.
 */
export class WebRTCService {
  private pc: RTCPeerConnection | null = null;
  private signaling: SignalingService;
  private turnCredentials: TurnCredentials | null = null;
  private onTrack: ((event: RTCTrackEvent) => void) | null = null;
  private onConnectionStateChange: ((state: string) => void) | null = null;
  private remoteDescriptionSet = false;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  // Serializes offer/answer cycles. The SFU renegotiates on EVERY subscribe/unsubscribe,
  // so offers can arrive back-to-back; the caller fires handleOffer without awaiting it.
  // Without this chain, concurrent setRemoteDescription/createAnswer/setLocalDescription
  // calls corrupt the PC signaling state, the answer is dropped, and the SFU stays wedged
  // in have-local-offer → new cameras never get a track (black screen / stuck "signaling")
  // and the node eventually times out and reconnects.
  private negotiationChain: Promise<void> = Promise.resolve();

  constructor(signaling: SignalingService) {
    this.signaling = signaling;
  }

  /**
   * Rebind to a new SignalingService after a signaling-only reconnect (the WS
   * dropped but this PC survived via session resume). Without this, answers and
   * ICE candidates from later renegotiations would be sent to the DEAD socket —
   * SignalingService.send() silently drops when the WS isn't OPEN — wedging the
   * SFU in have-local-offer and breaking every subsequent subscribe.
   */
  setSignaling(signaling: SignalingService): void {
    this.signaling = signaling;
  }

  async initialize(
    turnCredentials: TurnCredentials,
    onTrack: (event: RTCTrackEvent) => void,
    onConnectionStateChange?: (state: string) => void
  ): Promise<void> {
    this.turnCredentials = turnCredentials;
    this.onTrack = onTrack;
    this.onConnectionStateChange = onConnectionStateChange || null;

    // Create PeerConnection with TURN servers
    this.pc = new RTCPeerConnection({
      iceServers: turnCredentials.iceServers,
      iceTransportPolicy: 'all', // Try direct first, fall back to relay
      // Pre-gather ICE candidates (host/srflx + TURN allocation) at PC-creation
      // time, before the SFU's offer arrives. Since the SFU only offers on the
      // first subscribe, this overlaps the slow TURN-allocation round-trips with
      // the warm-up window so the first handshake isn't paying for them serially.
      iceCandidatePoolSize: 4,
    });

    // Handle incoming tracks (from SFU via TURN)
    this.pc.ontrack = (event) => {
      console.log('[WebRTC] Track received:', event.track.kind, event.track.id);
      if (this.onTrack) {
        this.onTrack(event);
      }
    };

    // Handle ICE candidates — send to cloud BE for relay to SFU
    this.pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.signaling.sendIceCandidate(event.candidate.toJSON());
      }
    };

    // Connection state monitoring
    this.pc.onconnectionstatechange = () => {
      const state = this.pc?.connectionState || 'unknown';
      console.log('[WebRTC] Connection state:', state);
      if (this.onConnectionStateChange) {
        this.onConnectionStateChange(state);
      }
    };

    this.pc.oniceconnectionstatechange = () => {
      console.log('[WebRTC] ICE state:', this.pc?.iceConnectionState);
    };

    // Add transceiver for receiving video/audio
    this.pc.addTransceiver('video', { direction: 'recvonly' });
    this.pc.addTransceiver('audio', { direction: 'recvonly' });
  }

  /**
   * Handle an SDP offer from the SFU (relayed through cloud BE).
   *
   * Chained onto negotiationChain so offer/answer cycles run STRICTLY one at a time even
   * when offers arrive back-to-back during rapid (un)subscribes. The chain survives a
   * failed cycle (.catch) so a single bad offer can't permanently block later ones.
   */
  async handleOffer(sdp: string): Promise<void> {
    const run = this.negotiationChain.then(() => this.processOffer(sdp));
    this.negotiationChain = run.catch(() => {});
    return run;
  }

  private async processOffer(sdp: string): Promise<void> {
    const pc = this.pc;
    if (!pc) throw new Error('PeerConnection not initialized');

    console.log(`[WebRTC] handleOffer: setRemoteDescription (signalingState=${pc.signalingState})`);
    await pc.setRemoteDescription(new RTCSessionDescription({
      type: 'offer',
      sdp,
    }));
    this.remoteDescriptionSet = true;
    console.log(`[WebRTC] handleOffer: remoteDescription set (signalingState=${pc.signalingState})`);

    // Flush any ICE candidates that arrived before the offer
    if (this.pendingCandidates.length > 0) {
      console.log(`[WebRTC] Flushing ${this.pendingCandidates.length} queued ICE candidates`);
      for (const c of this.pendingCandidates) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(c));
        } catch (err) {
          console.warn('[WebRTC] Failed to add queued ICE candidate:', err);
        }
      }
      this.pendingCandidates = [];
    }

    const answer = await pc.createAnswer();
    console.log(`[WebRTC] handleOffer: answer created (sdpLen=${answer.sdp?.length})`);
    await pc.setLocalDescription(answer);

    // Send answer back through signaling
    console.log(`[WebRTC] handleOffer: sending answer via signaling`);
    this.signaling.sendAnswer(answer.sdp!);
    console.log(`[WebRTC] handleOffer: answer sent, iceConnectionState=${pc.iceConnectionState}`);
  }

  /**
   * Handle an ICE candidate from the SFU.
   */
  async handleIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.pc) return;

    // Queue candidates that arrive before the remote description is set
    if (!this.remoteDescriptionSet) {
      this.pendingCandidates.push(candidate);
      return;
    }

    try {
      await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (err) {
      console.warn('[WebRTC] Failed to add ICE candidate:', err);
    }
  }

  getConnectionState(): string {
    return this.pc?.connectionState || 'new';
  }

  getIceConnectionState(): string {
    return this.pc?.iceConnectionState || 'new';
  }

  getStats(): Promise<RTCStatsReport> | null {
    return this.pc?.getStats() || null;
  }

  close(): void {
    if (this.pc) {
      // Nullify callbacks BEFORE closing to prevent stale events
      // (pc.close() fires onconnectionstatechange='closed' asynchronously)
      this.pc.ontrack = null;
      this.pc.onicecandidate = null;
      this.pc.onconnectionstatechange = null;
      this.pc.oniceconnectionstatechange = null;
      this.onTrack = null;
      this.onConnectionStateChange = null;
      this.pc.close();
      this.pc = null;
    }
  }

  getPeerConnection(): RTCPeerConnection | null {
    return this.pc;
  }
}
